import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Truck, Package, Zap, Globe2, Box, Layers, Snowflake, Warehouse, Network,
  ShieldCheck, LineChart, MapPin, ArrowRight, Star, Sparkles, Building2, Ship, Search, Calculator
} from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_apex-freight-1/artifacts/0mf1ps4m_WhatsApp%20Image%202026-06-25%20at%2010.16.47%20AM.jpeg";
const SHOWCASE_IMG = "https://customer-assets.emergentagent.com/job_apex-freight-1/artifacts/edmj38oj_WhatsApp%20Image%202026-06-25%20at%2010.16.47%20AM%20%281%29.jpeg";
const TECH_IMG = "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SERVICES = [
  { icon: Sparkles, title: "White Glove Service", desc: "Our specialty — precision handling, inside delivery, unpacking, assembly, debris removal. Concierge-grade freight." },
  { icon: Ship, title: "Drayage", desc: "Port-to-warehouse container moves through Port of Vancouver and beyond. FCL, LCL, and transloading covered." },
  { icon: Truck, title: "Full Truckload (FTL)", desc: "Dedicated capacity for high-volume shipments. Includes dry van, temperature-controlled, and expedited team-driver options across North America." },
  { icon: Package, title: "Less Than Truckload (LTL)", desc: "Cost-effective consolidated freight with dry van, reefer, and expedited service tiers — reliable transit and full visibility." },
  { icon: Globe2, title: "Cross-Border", desc: "Seamless US–Canada–Mexico moves with in-house customs expertise." },
  { icon: Layers, title: "Flatbed", desc: "Steel, machinery, building materials, and oversized loads." },
  { icon: Warehouse, title: "Warehousing", desc: "Strategic 3PL locations with cross-docking and fulfillment." },
  { icon: Network, title: "Supply Chain Solutions", desc: "Managed transportation, TMS integration, and network design." },
];

const INDUSTRIES = ["Retail & CPG", "Automotive", "Food & Beverage", "Pharma & Cold Chain", "Building Materials", "Manufacturing", "E-Commerce", "Chemicals"];

const STATS = [
  { n: "48", suffix: "states", label: "Coverage" },
  { n: "12,500+", suffix: "", label: "Vetted carriers" },
  { n: "98.6%", suffix: "", label: "On-time delivery" },
  { n: "24/7", suffix: "", label: "Live operations" },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

// Framer Motion prop objects — hoisted so they don't allocate on every render
const HERO_MOTION_INITIAL = { opacity: 0, y: 20 };
const HERO_MOTION_ANIMATE = { opacity: 1, y: 0 };
const HERO_MOTION_TRANSITION = { duration: 0.7 };
const SERVICES_MOTION_VIEWPORT = { once: true, amount: 0.15 };

export default function Home() {
  const navigate = useNavigate();
  const [trackId, setTrackId] = useState("");
  const goTrack = (e) => {
    e.preventDefault();
    const q = trackId.trim();
    if (!q) return;
    navigate(`/track?id=${encodeURIComponent(q)}`);
  };
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden text-white hero-grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Freight moving across America" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B3D6E]/95 via-[#0B3D6E]/80 to-[#0B3D6E]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-28 lg:pt-32 lg:pb-36">
          <motion.div initial={HERO_MOTION_INITIAL} animate={HERO_MOTION_ANIMATE} transition={HERO_MOTION_TRANSITION} className="max-w-2xl">
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[1.02]">
              Moving Freight.<br />
              <span className="text-[#2E7BC7]">Building Partnerships.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-slate-200/90">
              Reliable, transparent, cost-effective transportation across the US, Canada, and Mexico —
              engineered around your supply chain, not ours.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/quote" className="btn-primary-apex inline-flex items-center gap-2 justify-center" data-testid="hero-get-quote-btn">
                Get a Quote <ArrowRight className="h-4 w-4 drive" />
              </Link>
              <Link to="/track" className="btn-ghost-white inline-flex items-center gap-2 justify-center" data-testid="hero-track-btn">
                <MapPin className="h-4 w-4" /> Track Shipment
              </Link>
            </div>

            {/* Instant tracking search — connected to TMS */}
            <form
              onSubmit={goTrack}
              className="mt-8 max-w-xl bg-white/95 backdrop-blur-sm rounded-md p-2 flex items-center gap-2 shadow-lg border border-white/20"
              data-testid="hero-track-form"
            >
              <div className="pl-2 text-slate-400">
                <Search className="h-5 w-5" />
              </div>
              <input
                type="text"
                value={trackId}
                onChange={(e) => setTrackId(e.target.value)}
                placeholder="Enter tracking / Job # (e.g. APX-100017)"
                className="flex-1 bg-transparent text-slate-800 placeholder-slate-400 px-2 py-2 outline-none text-sm sm:text-base"
                aria-label="Tracking number"
                data-testid="hero-track-input"
              />
              <button
                type="submit"
                className="bg-[#E76F00] hover:bg-[#c95f00] text-white font-bold text-sm px-4 py-2 rounded transition-colors inline-flex items-center gap-1"
                data-testid="hero-track-submit-btn"
              >
                Track <ArrowRight className="h-4 w-4" />
              </button>
            </form>
            <div className="mt-2 text-xs text-slate-300/80 max-w-xl">
              Real-time status pulled directly from our TMS. Try a Job # or shipment reference.
            </div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <div className="relative bg-[#0B3D6E] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-white">
                <div className="font-display font-black text-3xl md:text-4xl">{s.n} <span className="text-[#2E7BC7] text-lg font-semibold">{s.suffix}</span></div>
                <div className="mt-1 text-xs font-label text-slate-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="font-label text-xs text-[#2E7BC7]">What we move</div>
              <h2 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E] max-w-2xl">Every mode. Every mile. Every commitment.</h2>
            </div>
            <p className="max-w-md text-slate-600">A full suite of freight services under one accountable team — with the technology, capacity, and coverage to scale with you.</p>
          </div>

          <motion.div variants={container} initial="hidden" whileInView="show" viewport={SERVICES_MOTION_VIEWPORT}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map(({ icon: Icon, title, desc }, i) => {
              const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <motion.div key={title} variants={item}
                  className="group bg-white border border-gray-200 rounded-lg p-7 hover:-translate-y-1 hover:border-[#2E7BC7]/40 hover:shadow-md transition-all flex flex-col"
                  data-testid={`service-card-${i}`}>
                  <div className="h-11 w-11 rounded-md bg-[#0B3D6E] flex items-center justify-center group-hover:bg-[#2E7BC7] transition-colors">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-lg text-[#0B3D6E]">{title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{desc}</p>
                  <div className="mt-5 flex gap-2">
                    <Link to={`/book?service=${encodeURIComponent(title)}`} className="text-xs font-bold text-white bg-[#2E7BC7] hover:bg-[#1E5A9A] px-3 py-1.5 rounded transition-colors" data-testid={`home-service-book-${slug}`}>Book Load</Link>
                    <Link to={`/quote?service=${encodeURIComponent(title)}`} className="text-xs font-bold text-[#0B3D6E] border border-[#0B3D6E] hover:bg-[#0B3D6E] hover:text-white px-3 py-1.5 rounded transition-colors" data-testid={`home-service-quote-${slug}`}>Ask Quote</Link>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* INDUSTRIES TICKER */}
      <section className="bg-[#0B3D6E] py-8 overflow-hidden">
        <div className="marquee-track flex gap-14 items-center">
          {[...INDUSTRIES, ...INDUSTRIES, ...INDUSTRIES].map((i, idx) => (
            <span key={`${i}-${idx}`} className="font-display text-2xl md:text-3xl font-black text-white/10 whitespace-nowrap">
              {i} <span className="text-[#2E7BC7]">/</span>
            </span>
          ))}
        </div>
      </section>

      {/* TRUSTED PARTNERS */}
      <section className="py-16 bg-white border-b border-gray-100" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="font-label text-xs text-[#2E7BC7]">Proudly working with</div>
            <h2 className="mt-3 font-display font-black text-2xl md:text-3xl text-[#0B3D6E]">Partners who trust Apex with their freight</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "Westcoast Plastic Recycling",
              "Zoom Books Donations",
              "Treadstone Logistics",
              "a2z Specialty Wholesales Ltd",
            ].map((p, i) => (
              <div key={p} className="border border-gray-200 rounded-lg py-8 px-4 text-center hover:border-[#2E7BC7]/60 hover:-translate-y-0.5 transition-all" data-testid={`partner-${i}`}>
                <div className="font-display font-black text-lg text-[#0B3D6E] leading-tight">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAREHOUSES */}
      <section className="py-20 lg:py-28 bg-gray-50" data-testid="warehouses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="font-label text-xs text-[#2E7BC7]">Our warehouse network</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E]">310,000 sq ft of Canadian 3PL capacity.</h2>
            <p className="mt-5 text-slate-600">Cross-docking, storage, pick-and-pack, and fulfillment — strategically located to serve Western, Central, and Eastern Canada.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { city: "Vancouver", region: "British Columbia", sqft: "200,000", tag: "West Coast Hub" },
              { city: "Calgary", region: "Alberta", sqft: "50,000", tag: "Prairies Gateway" },
              { city: "Milton", region: "Ontario", sqft: "60,000", tag: "GTA Distribution" },
            ].map((w, i) => (
              <div key={w.city} className="relative bg-white border border-gray-200 rounded-lg p-8 hover:border-[#2E7BC7]/40 hover:-translate-y-1 transition-all" data-testid={`warehouse-${i}`}>
                <div className="flex items-start justify-between">
                  <Building2 className="h-8 w-8 text-[#2E7BC7]" />
                  <span className="pill bg-blue-100 text-blue-800">{w.tag}</span>
                </div>
                <div className="mt-6 font-display font-black text-2xl text-[#0B3D6E]">{w.city}</div>
                <div className="text-sm text-slate-500 mt-1">{w.region}</div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="font-label text-xs text-slate-500">Facility size</div>
                  <div className="mt-1 font-display font-black text-3xl text-[#0B3D6E]">{w.sqft} <span className="text-sm font-semibold text-slate-500">sq ft</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section className="py-20 lg:py-28 bg-gray-50" data-testid="technology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="font-label text-xs text-[#2E7BC7]">Technology</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E]">A TMS-ready platform, built for shippers and carriers.</h2>
            <p className="mt-6 text-slate-600 max-w-lg">
              Real-time visibility, EDI/API-first integrations, and dedicated Customer and Carrier Portals.
              Connect to QuickBooks, your CRM, and any modern TMS.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { icon: MapPin, t: "Live tracking", d: "Continuous GPS + geo-fenced milestones." },
                { icon: ShieldCheck, t: "Secure portals", d: "SOC 2-aligned customer & carrier access." },
                { icon: LineChart, t: "Analytics", d: "Lane, cost, and OTIF dashboards." },
                { icon: Network, t: "Open APIs", d: "REST + webhooks ready for your stack." },
              ].map(({ icon: I, t, d }) => (
                <div key={t} className="bg-white border border-gray-200 rounded-md p-4">
                  <I className="h-5 w-5 text-[#2E7BC7]" />
                  <div className="mt-3 font-display font-bold text-[#0B3D6E]">{t}</div>
                  <div className="text-sm text-slate-600">{d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={TECH_IMG} alt="Supply chain container yard" className="rounded-lg shadow-lg w-full h-[460px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-5 max-w-xs hidden md:block border border-gray-100">
              <div className="font-label text-xs text-[#2E7BC7]">Case study</div>
              <div className="mt-1 font-display font-bold text-[#0B3D6E]">32% freight cost reduction</div>
              <div className="text-xs text-slate-600 mt-1">Mid-market retailer, 18 lanes managed by Apex.</div>
            </div>
          </div>
        </div>
      </section>

      {/* DUTY & TAX CALCULATOR (Zipments Widget) */}
      <section className="py-20 lg:py-28 bg-white border-t border-b border-gray-100" data-testid="duty-tax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="font-label text-xs text-[#2E7BC7] flex items-center gap-2">
              <Calculator className="h-3.5 w-3.5" /> Cross-Border Tools
            </div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E]">Free Duty &amp; Tax Calculator.</h2>
            <p className="mt-4 text-slate-600">
              Estimate landed cost for shipments into Canada or the US instantly. No signup, no cost — powered by our partners at Zipments.
            </p>
          </div>
          <div className="relative bg-gray-50 border border-gray-200 rounded-lg overflow-hidden shadow-sm" data-testid="duty-tax-widget">
            <div className="bg-[#0B3D6E] text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-[#2E7BC7]" />
                <span className="font-display font-bold text-sm tracking-wide">Duty &amp; Tax Estimator</span>
              </div>
              <div className="text-[10px] font-label tracking-[0.15em] uppercase text-slate-300">Live · Powered by Zipments</div>
            </div>
            <iframe
              id="zipments-web-widget-iframe"
              src="https://widgets.zipments.io/064d727dd7/duty-tax-quote"
              width="100%"
              height="900px"
              title="Zipments Duty & Tax Calculator"
              frameBorder="0"
              allow="clipboard-write; fullscreen; web-share"
              className="block w-full bg-white"
            />
          </div>
          <p className="mt-4 text-xs text-slate-500 max-w-2xl">
            Estimates are for reference. Actual duties and taxes are determined by customs at the time of import. Contact our cross-border team for firm quotes.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="font-label text-xs text-[#2E7BC7]">Trusted by operators</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl text-[#0B3D6E]">The freight partner shippers keep.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { q: "Apex became an extension of our supply chain team within weeks. Communication is unreal.", a: "VP Operations, National Retailer" },
              { q: "The best combination of price, service, and technology we've worked with in 12 years.", a: "Director of Logistics, CPG" },
              { q: "Their carrier network unlocked lanes our incumbent 3PL couldn't cover reliably.", a: "COO, Manufacturing" },
            ].map((t, i) => (
              <div key={t.a} className="border border-gray-200 rounded-lg p-8 bg-white hover:border-[#2E7BC7]/40 transition-colors" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5">{[0,1,2,3,4].map((k) => <Star key={`${t.a}-star-${k}`} className="h-4 w-4 fill-[#2E7BC7] text-[#2E7BC7]" />)}</div>
                <p className="mt-5 text-slate-800 leading-relaxed">"{t.q}"</p>
                <div className="mt-6 font-label text-xs text-slate-500">{t.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#0B3D6E] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display font-black text-3xl md:text-5xl">Ready to move?</h2>
            <p className="mt-4 text-slate-300 max-w-md">Tell us about your lanes. We'll respond with a firm quote within 30 minutes.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <Link to="/quote" className="btn-primary-apex" data-testid="cta-quote-btn">Request a Quote</Link>
            <Link to="/contact" className="btn-ghost-white" data-testid="cta-contact-btn">Talk to Sales</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
